// پروفایل — what the profile screen shows, and where each fact comes from.
//
// ── Two halves, and only one of them is editable ────────────────────────
//
// v2 DOES have a profile table (`student_profiles`, core.md §5.2) and the seven
// fields on it are edited here through `updateStudentProfile` — that is
// `ProfileIdentityForm`, which renders the onboarding form a second time so the
// rules cannot drift.
//
// What is below is the other half: the ACCOUNT facts, every one of them owned
// by something else. The phone belongs to Keycloak, the patient count to the
// student service, the balance to the wallet, and the id is the token's `sub`.
// None of them is editable from this app, and `profile.identity.note` says
// where the phone IS edited.
//
// The appearance choices below are the exception, and they are LOCAL. The doctor
// and dentist panels persist theme/font/language to their BFF so the preference
// follows the user to another machine; the frozen contract has no such mutation,
// so this app caches them in the browser and says so
// (`profile.appearance.localOnly`). Inventing a mutation for it would be a change
// to all three contract files.

// The phone is stored canonically (`+989121234567`) and Iranians read and type
// `09121234567`, so the stored shape is formatted back for display. `forDisplay`
// is the kit's one READ-path renderer for that rule — a sixth hand-written copy is
// exactly what this change exists to end. It never blanks and never refuses: a
// value it cannot parse comes back exactly as stored, which is the only thing a
// read surface can honestly do with a column somebody already wrote.
//
// The DEEP path rather than the barrel: this is a logic module with no JSX, and
// the barrel would drag the kit's React layer into anything that imports it. The
// barrel is literally `export * as phone from './lib/phone'`, so this namespace
// IS the `phone` namespace every other panel imports.
//
// KNOWN, 2026-09-13: `ui/lib/phone.js` is not in the framework this app has
// installed, nor in the tag package.json now pins (`#v0.14.0` is not cut yet), so
// this import does not resolve today. It is written anyway — the account, clinic,
// doctor, patient and dentist panels are all written against the same export in
// the same position, and the whole chain unblocks on one tag.
import * as phone from '@parsina/framework/ui/lib/phone.js';

/** The identity rows, in reading order. `value` is resolved by the surface. */
export const IDENTITY_FIELDS = [
  { id: 'phone', labelKey: 'profile.fields.phone', ltr: true },
  { id: 'studentId', labelKey: 'profile.fields.studentId', ltr: true },
  { id: 'cases', labelKey: 'profile.fields.cases' },
  { id: 'balance', labelKey: 'profile.fields.balance' },
];

/**
 * The identity row values for a profile + balance.
 *
 * `id` is the Keycloak `sub` — the owner id on every case, entry and message this
 * student has ever written. It is shown because it is what support asks for, and
 * it is shown LTR because it is a UUID.
 */
export function identityValues({ me, balance, t, formatNumber, formatDigits, fmtParseh }) {
  return {
    // `forDisplay` returns ASCII, so `formatDigits` still runs AFTER it — the row
    // reads in Persian digits like every other value on this screen.
    phone: me?.phone ? formatDigits(phone.forDisplay(me.phone)) : '',
    studentId: me?.id || '',
    cases: me?.caseCount == null ? '' : formatNumber(me.caseCount),
    balance:
      balance == null ? '' : `${fmtParseh(balance, formatNumber)} ${t('common.units.parseh')}`,
  };
}

export default IDENTITY_FIELDS;
